import { Link, useLocation } from "react-router-dom";
import { goTo, goTop } from "@/lib/scroll";
import { Flag } from "./Flag";
import { SITE } from "@/constants/site";
import useI18n from "@/i18n";
import { equivalentPath, LANGS, LANG_LABELS, pagePath } from "@/i18n/config";

const linkId = (hash) => `nav-link-${hash.replace("#", "")}`;

const LangSwitch = ({ lang, pathname, aria, suffix = "" }) => (
    <nav
        aria-label={aria}
        className="flex items-center gap-1"
        data-testid={`lang-switch${suffix}`}
    >
        {LANGS.map((l) => (
            <Link
                key={l}
                to={equivalentPath(pathname, l)}
                data-testid={`lang-link-${l}${suffix}`}
                aria-current={l === lang ? "true" : undefined}
                className="flex items-center gap-1.5 rounded-full px-2 py-1 text-[12px] font-bold uppercase tracking-wider transition-colors duration-200"
                style={
                    l === lang
                        ? { background: "var(--c-terra)", color: "#FFFFFF" }
                        : { color: "var(--c-ink2)" }
                }
            >
                <Flag lang={l} width={18} />
                <span className={suffix ? "" : "hidden lg:inline"}>{LANG_LABELS[l]}</span>
            </Link>
        ))}
    </nav>
);

export default function Header() {
    const { pathname } = useLocation();
    const { t, lang } = useI18n();
    const home = pagePath(lang, "home");
    const surAccueil = pathname === home || pathname === `${home}/`;

    const PAGES = [
        [t.nav.tarifs, pagePath(lang, "tarifs"), "nav-link-tarifs"],
        [t.nav.cabinet, pagePath(lang, "cabinet"), "nav-link-lieu"],
        [t.nav.medecin, pagePath(lang, "medecin"), "nav-link-medecin"],
    ];
    const ANCRES = [
        [t.nav.approche, "#approche"],
        [t.nav.soins, "#soins"],
    ];

    /* Depuis une page intérieure, les ancres doivent repasser par l'accueil. */
    const NavLien = ({ hash, label, className }) =>
        surAccueil ? (
            <a
                href={hash}
                onClick={(e) => goTo(e, hash)}
                data-testid={linkId(hash)}
                className={className}
            >
                {label}
            </a>
        ) : (
            <Link
                to={`${home === "/" ? "" : home}/${hash}`}
                data-testid={linkId(hash)}
                className={className}
            >
                {label}
            </Link>
        );

    return (
        <header
            className="fixed inset-x-0 top-0 z-50"
            style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(251,247,242,0.88) 100%)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                borderBottom: "1px solid var(--c-line)",
                boxShadow: "0 10px 30px -26px rgba(59,42,34,0.45)",
            }}
        >
            <div className="mx-auto max-w-[1080px] px-5">
                <div className="flex h-16 items-center justify-between">
                    {surAccueil ? (
                        <a
                            href="#top"
                            onClick={goTop}
                            data-testid="header-logo"
                            className="mr-3 whitespace-nowrap font-display text-[18px] text-terrat sm:text-[22px]"
                        >
                            {SITE.name}
                        </a>
                    ) : (
                        <Link
                            to={home}
                            data-testid="header-logo"
                            className="mr-3 whitespace-nowrap font-display text-[18px] text-terrat sm:text-[22px]"
                        >
                            {SITE.name}
                        </Link>
                    )}
                    <div className="flex items-center gap-4 md:gap-6">
                        <nav className="hidden items-center gap-5 md:flex lg:gap-7">
                            {ANCRES.map(([label, hash]) => (
                                <NavLien
                                    key={hash}
                                    hash={hash}
                                    label={label}
                                    className="whitespace-nowrap text-[15px] font-semibold text-ink transition-colors duration-200 hover:text-terrat"
                                />
                            ))}
                            {PAGES.map(([label, to, testid]) => (
                                <Link
                                    key={to}
                                    to={to}
                                    data-testid={testid}
                                    className="whitespace-nowrap text-[15px] font-semibold text-ink transition-colors duration-200 hover:text-terrat"
                                >
                                    {label}
                                </Link>
                            ))}
                            <NavLien
                                hash="#faq"
                                label={t.nav.faq}
                                className="whitespace-nowrap text-[15px] font-semibold text-ink transition-colors duration-200 hover:text-terrat"
                            />
                        </nav>
                        <div className="hidden sm:block">
                            <LangSwitch lang={lang} pathname={pathname} aria={t.nav.langAria} />
                        </div>
                        <a
                            href={surAccueil ? "#rdv" : `${home === "/" ? "" : home}/#rdv`}
                            onClick={(e) => surAccueil && goTo(e, "#rdv")}
                            data-testid="header-rdv-button"
                            className="btn btn-primary btn-sm shrink-0 sm:px-6 sm:py-3 sm:text-[15px]"
                        >
                            <span className="sm:hidden">{t.nav.rdvShort}</span>
                            <span className="hidden sm:inline">{t.nav.rdvLong}</span>
                        </a>
                    </div>
                </div>
                <nav className="no-scrollbar -mx-1 overflow-x-auto pb-2 md:hidden">
                    <div className="flex w-max items-center gap-5 px-1">
                        {ANCRES.map(([label, hash]) => (
                            <NavLien
                                key={hash}
                                hash={hash}
                                label={label}
                                className="whitespace-nowrap text-[13px] font-semibold text-ink transition-colors duration-200 hover:text-terrat"
                            />
                        ))}
                        {PAGES.map(([label, to, testid]) => (
                            <Link
                                key={to}
                                to={to}
                                data-testid={`${testid}-mobile`}
                                className="whitespace-nowrap text-[13px] font-semibold text-ink transition-colors duration-200 hover:text-terrat"
                            >
                                {label}
                            </Link>
                        ))}
                        <NavLien
                            hash="#faq"
                            label={t.nav.faq}
                            className="text-[13px] font-semibold text-ink transition-colors duration-200 hover:text-terrat"
                        />
                        <span className="sm:hidden">
                            <LangSwitch
                                lang={lang}
                                pathname={pathname}
                                aria={t.nav.langAria}
                                suffix="-mobile"
                            />
                        </span>
                    </div>
                </nav>
            </div>
        </header>
    );
}
